import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { refreshSettings } from "@/lib/site";

const FIELDS = [
    ["hero_headline", "Homepage headline (use a line break for the second line)", true],
    ["hero_subheading", "Homepage subheading", true],
    ["intro_text", "Homepage introduction", true],
    ["contact_email", "Contact email (receives all inquiries)"],
    ["instagram_handle", "Instagram handle (e.g. @pieter)"],
    ["instagram_url", "Instagram URL"],
    ["taylor_brokers_url", "Taylor Brokers URL"],
    ["verde_capital_url", "Verde Capital URL"],
    ["nicoline_url", "Nicoline Beerkens Interiors URL"],
    ["news_url", "Get The Plot URL"],
    ["linkedin_url", "LinkedIn URL"],
];

export default function AdminSettings({ client }) {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { client.get("/settings").then(({ data }) => setS((prev) => prev || data)); }, [client]);
    if (!s) return <Loader2 className="h-6 w-6 animate-spin text-forest" />;

    const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));
    const setVideo = (i, k, v) => setS((p) => ({ ...p, walk_videos: p.walk_videos.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));

    const uploadCover = async (i, e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await client.post("/admin/upload", fd);
            setVideo(i, "cover", data.url);
            toast.success("Cover uploaded — remember to save");
        } catch { toast.error("Cover upload failed"); }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await client.put("/admin/settings", s);
            setS(data);
            refreshSettings();
            toast.success("Saved");
        } catch { toast.error("Save failed — check the fields"); } finally { setSaving(false); }
    };

    return (
        <div data-testid="admin-settings" className="max-w-3xl space-y-6">
            {FIELDS.map(([k, label, long]) => (
                <label key={k} className="block">
                    <span className="mono-label text-muted_text mb-2 block">{label}</span>
                    {long
                        ? <textarea rows={3} value={s[k] || ""} onChange={set(k)} className="editorial-input" data-testid={`admin-setting-${k}`} />
                        : <input value={s[k] || ""} onChange={set(k)} className="editorial-input" data-testid={`admin-setting-${k}`} />}
                </label>
            ))}
            <div>
                <p className="mono-label text-muted_text mb-2">Walk the Land reels — Instagram reel URL, title and a portrait cover image (max 3 shown; without a cover a site image is used)</p>
                {(s.walk_videos || []).map((v, i) => (
                    <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2 mb-3">
                        {v.cover ? <img src={v.cover} alt="" className="h-14 w-10 object-cover rounded" /> : <span className="h-14 w-10 rounded bg-surface shrink-0" />}
                        <input placeholder="https://www.instagram.com/reel/…" value={v.url || ""} onChange={(e) => setVideo(i, "url", e.target.value)} className="editorial-input" data-testid={`admin-video-url-${i}`} />
                        <input placeholder="Title" value={v.title || ""} onChange={(e) => setVideo(i, "title", e.target.value)} className="editorial-input" data-testid={`admin-video-title-${i}`} />
                        <label className="shrink-0 inline-flex items-center gap-1 rounded-full border border-forest/40 px-3 py-2 text-xs cursor-pointer" data-testid={`admin-video-cover-${i}`}>
                            <Upload className="h-3.5 w-3.5" /> Cover
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(i, e)} />
                        </label>
                        <button type="button" onClick={() => setS((p) => ({ ...p, walk_videos: p.walk_videos.filter((_, j) => j !== i) }))} aria-label="Remove video" className="px-3 text-muted_text hover:text-forest"><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
                <button type="button" onClick={() => setS((p) => ({ ...p, walk_videos: [...(p.walk_videos || []), { url: "", title: "" }] }))}
                        className="inline-flex items-center gap-2 text-sm text-forest" data-testid="admin-video-add"><Plus className="h-4 w-4" /> Add video</button>
            </div>
            <button type="button" onClick={save} disabled={saving} data-testid="admin-settings-save"
                    className="rounded-full bg-forest text-cream px-8 py-3.5 text-sm font-medium disabled:opacity-60">{saving ? "Saving…" : "Save content & links"}</button>
        </div>
    );
}
